import { useEffect, useState } from 'react';
import apis from '../../../utils/apis';
import {
  PeopleDirectory,
  EMPTY_PEOPLE_DIRECTORY,
  buildPeopleDirectory,
} from './peopleDirectory';

/**
 * The organization's people, fetched once for a whole page.
 *
 * One request, two answers - the pictures a list renders and the uuids its
 * links use (see `peopleDirectory`). The list and a detail opened from it both
 * want it, and the section swaps one for the other in the same shell, so the
 * in-flight request is shared at module level rather than made twice.
 *
 * Never throws and never blocks. `ready` says whether the answer has ARRIVED,
 * which is not the same as whether it found anything: a caller resolving a uuid
 * has to wait for the payload before it may conclude "no such person", and a
 * failed request must end that wait rather than extend it forever.
 */

/** Matches `useOrgMembers`; enough for the org sizes the admin lists serve. */
const PEOPLE_LIMIT = 500;

export interface PeopleDirectoryState {
  directory: PeopleDirectory;
  /** True once the request has settled - successfully or not. */
  ready: boolean;
}

let inFlight: Promise<PeopleDirectory> | null = null;

const fetchDirectory = (): Promise<PeopleDirectory> => {
  if (!inFlight) {
    inFlight = apis
      .get('/org-people/people', { params: { limit: PEOPLE_LIMIT, page: 1 } })
      .then((response) => buildPeopleDirectory(response.data?.people || response.data?.data || []))
      .catch(() => EMPTY_PEOPLE_DIRECTORY)
      // A failure must not be remembered as the answer: the next page that asks
      // gets a fresh request rather than a permanently empty directory.
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

const usePeopleDirectory = (): PeopleDirectoryState => {
  const [state, setState] = useState<PeopleDirectoryState>({
    directory: EMPTY_PEOPLE_DIRECTORY,
    ready: false,
  });

  useEffect(() => {
    let alive = true;
    fetchDirectory().then((directory) => {
      if (alive) setState({ directory, ready: true });
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
};

export default usePeopleDirectory;
